import { useMutation, useQueryClient } from "@tanstack/react-query";
import { getBackendUrl } from "api/common";
import { mutationFnPOST } from "api/tanStackQuery/helpers";
import { ResponseWithVisibility } from "features/CenterContent/RoleContent/InternalMessage/utils/queries";
import {
  WithInternalMessages,
  withInternalMessages,
} from "features/CenterContent/RoleContent/InternalMessage/utils/withInternalMessages";
import { EditTrainInfoModal } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/EditTrainInfoModal";
import { TrainEventTypeEnum } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/types/trainEventTypeEnum";
import {
  TrainInfoMessage as TrainInfoMsg,
  TrainInformationAggregated,
  TrainInfoState,
} from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/types/trainInformationAggregated";
import { getUrgencySemanticsMapping } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/utils";
import { DetailsParams } from "features/CenterContent/VehicleDetails/TrainDetails/TrainDetails";
import { routeChangedEventsQueryKey } from "features/CenterContent/VehicleDetails/TrainDetails/useRouteChangedEvents";
import { EventInfoMessage } from "features/CenterContent/VehicleDetails/shared/EventInfoMessage";
import { DropsLogTextInput } from "features/CenterContent/shared/DropsLogTextInput";
import { supportedOpInfoTypes } from "features/CenterContent/shared/operationalInformation/supportedOpInfoTypes";
import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { FailureMessage } from "shared/components/feedback/FailureMessage/FailureMessage";
import { useCountryCodeForDropsRole } from "shared/hooks/useCountryCodeForDropsRole";
import { formatDayDateString } from "shared/utils/dateTimeUtils";

type TrainInfoMessageProps = {
  info: WithInternalMessages<TrainInformationAggregated>;
  trainInfoWithOpenState: string[];
};

export const TrainInfoMessage: FC<TrainInfoMessageProps> = ({
  info,
  trainInfoWithOpenState,
}) => {
  const { t } = useTranslation();
  const { trainNumber, trainDate } = useParams<DetailsParams>();
  const queryClient = useQueryClient();
  const countryCodeForDropsRole = useCountryCodeForDropsRole();

  const [dropsLogText, setDropsLogText] = useState<string | null>(null);
  const { mutateAsync: closeTrainStoppedStatus } = useMutation({
    mutationKey: ["closeTrainStoppedStatus", info.latestInfoUuid],
    mutationFn: () =>
      mutationFnPOST<ResponseWithVisibility, { dropsLogText?: string }>(
        `${getBackendUrl()}/trainInformation/information/${info.latestInfoUuid}/close`,
        dropsLogText ? { dropsLogText } : {},
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [routeChangedEventsQueryKey, trainNumber, trainDate],
      });
    },
  });

  const [askConfirmation, setAskConfirmation] = useState<boolean>(false);

  const indexOfLatestInfoUuid = info.versions.findIndex(
    (version) => version.uuid === info.latestInfoUuid,
  );

  // For some event types (TRAIN_STOPPED, TRAIN_INCREASED_CAPACITY, TRAIN_REDUCED_CAPACITY, TRAIN_MISSING_PRODUCT), multiple events for the same train is not supported.
  // Creating a new event will be considered a new version of the previous event of the same type, even if it is closed or replaced.
  // This can in some edge cases lead to all events being considered related to each other, leading to duplicate info lists with too many versions.
  // Therefore, we need to ensure that the the newest version uuid matches latestInfoUuid.
  // This is to ensure that all non-replaced versions are displayed on top, so that they can be closed or edited.
  const infoWithAllVersionsUpToLatestInfoUuid = {
    ...info,
    versions: info.versions.slice(indexOfLatestInfoUuid),
  };

  const { versions } = infoWithAllVersionsUpToLatestInfoUuid;

  const newestVersion = versions[0];

  const { references } = newestVersion;
  const productionDeviationUuid = references?.find(
    (it) => it.type === "PRODUCTION_DEVIATION_REF",
  )?.id;

  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const showActionButtons =
    newestVersion.state === TrainInfoState.OPEN &&
    (supportedOpInfoTypes.includes(newestVersion.type) ||
      newestVersion.type === "TRAIN_GENERAL_INFO" ||
      newestVersion.type === "TRAIN_GENERAL_WARNING");

  const getTitle = (trainInfoMessage: TrainInfoMsg): string => {
    const title = trainInfoMessage.title ?? t("Generell informasjon");
    switch (trainInfoMessage.state) {
      case TrainInfoState.CLOSED:
        return `${title} [${t("Friskmeldt")}]`;
      case TrainInfoState.DEPUBLISHED:
        return `${title} [${t("Depublisert")}]`;
      case TrainInfoState.REPLACED:
        return `${title} [${t("Erstattet")}]`;
      case TrainInfoState.OPEN:
        return title;
    }
  };

  if (trainNumber === undefined || trainDate === undefined) {
    return (
      <FailureMessage
        customMessage={t(
          "Kunne ikke hente tognummer eller dato for dette toget.",
        )}
      />
    );
  }

  const addInternalMessageToNewestVersion = ([
    latestVersion,
    ...prevVersions
  ]: TrainInfoMsg[]) => [
    withInternalMessages(latestVersion, info.internalMessages),
    ...prevVersions,
  ];

  return (
    <>
      <EventInfoMessage
        title={getTitle(newestVersion)}
        subTitle={formatDayDateString(newestVersion.updatedAt)}
        semantics={getUrgencySemanticsMapping(versions[0])}
        versions={addInternalMessageToNewestVersion(versions)}
        showActionButtons={showActionButtons}
        askConfirmation={askConfirmation}
        setAskConfirmation={setAskConfirmation}
        setModalOpen={setModalOpen}
        onClose={async () => closeTrainStoppedStatus()}
        dropsLogTextInput={
          <DropsLogTextInput
            dropsLogText={dropsLogText}
            setDropsLogText={setDropsLogText}
            uuid={info.latestInfoUuid}
            defaultEnabled={
              (newestVersion.type as TrainEventTypeEnum) ===
              TrainEventTypeEnum.TRAIN_STOPPED // We almost always want "Tog står" to go to drops log
            }
          />
        }
        productionDeviationUuid={productionDeviationUuid}
      />
      {modalOpen && (
        <EditTrainInfoModal
          uuid={info.latestInfoUuid}
          setModalOpen={setModalOpen}
          trainId={trainNumber}
          nominalDate={trainDate}
          countryCode={countryCodeForDropsRole}
          trainInfoWithOpenState={trainInfoWithOpenState}
          internalMessages={info.internalMessages}
        />
      )}
    </>
  );
};
